import {
  Box,
  Grid,
  IconButton,
  Popover,
  useTheme,
  Tooltip
} from '@material-ui/core';
import { useParams } from 'react-router-dom';
import { memo, useMemo, useState } from 'react';

import {
  AIProductMatchBadge,
  SButton,
  SETVIImage,
  SHighlightText,
  SText
} from '@setvi/shared/components';
import ExpandMoreRoundedIcon from '@material-ui/icons/ExpandMoreRounded';
import { useDialog } from '@setvi/shared/providers';
import {
  AnalyticsModules,
  useAnalytics,
  useSubscribedMutation
} from '@setvi/shared/hooks';
import {
  AutoRFQQueryKey,
  createInternalNoteMutation,
  deleteCustomerNoteMutation,
  deleteInternalNoteMutation,
  saveCustomerNoteMutation,
  updateInternalNoteMutation
} from 'Services';
import { Product } from '@setvi/shared/interfaces';
import {
  getProductStatus,
  ImagePreview,
  ProductLabel,
  ProductStatusBadge
} from 'componentsV2';

import ManualEntry, {
  ManualProps
} from 'pages/auto-rfq/pages/details/components/manual-entry';
import { Stock } from 'pages/products/components/stock';
import { RfqMatchOverride, RFQProducts } from 'pages/auto-rfq/interfaces';
import { useRFQStore } from 'pages/auto-rfq/store';
import { AdditionalContent } from 'pages/products/components/products-list/components';
import { getStockInfo } from 'pages/products/components/products-list/useProductColumns/contents/stock-info';
import {
  BaseDialog,
  PrivateLabels
} from 'pages/auto-rfq/pages/details/components';
import { Tabs } from 'pages/auto-rfq/pages/details/components/base-dialog';

import { toTitleCase } from '@setvi/shared/utils/formatCustomerData';
import {
  CustomerNoteIcon,
  InternalNoteIcon,
  SlashedFileIconV2
} from '@setvi/shared/components/sicons';
import { useRfqConfigStore } from 'pages/auto-rfq/store/rfqConfig';
import { useCatalogConfig } from 'pages/products/hooks/useCatalogConfig';
import { Container, FlexRow, iconStyle, RemovedBadge } from './styled';
import {
  Competitor,
  ExternalChip,
  MatchIndicator,
  NoMatch,
  PricingDisplay
} from './components';
import CustomerNoteDialog from './components/customer-note-dialog';
import InternalNoteDialog from './components/internal-note-dialog';
import { AnchorBtn } from '../private-labels/styled';

export interface MatchedProduct extends Partial<Product> {
  lastModifiedDate?: string;
  isStockProduct?: boolean;
  external?: boolean;
}

export interface OtherMatchesProps {
  _id: string;
  id: string;
  reasoning?: string;
  percentage?: number;
  isPrivateLabel?: boolean;
  order: number;
}

export interface ProductCardProps extends RFQProducts {
  totalMatched: number;
  selected?: boolean;
  matched: MatchedProduct;
  search: string;
  index: number;
  order?: number;
  hasPrivateLabels?: boolean;
  internalProducts?: OtherMatchesProps[];
  actionButton?: JSX.Element;
  expandedView?: boolean;

  onSelect?: (checked: boolean) => void;
  onIgnore: () => void;
  showMoreProducts: (i: number) => void;
  handleManualEntry: (props: ManualProps, index: number) => void;
  openProductDetails: () => void;
  handleProductChange: (
    product: Product,
    index: number,
    isExternal?: boolean
  ) => void;
}

const ProductCard = ({
  manufacturerSku,
  mfgSkuMatched,
  foundOnline,
  actionButton,
  hasPrivateLabels,
  internalProducts,
  totalMatched,
  manualEntry,
  foundItem,
  selected,
  ignored,
  matched,
  search,
  index,
  order,
  sell,
  expandedView = true,
  handleProductChange,
  openProductDetails,
  handleManualEntry,
  showMoreProducts,
  onSelect,
  ...rest
}: ProductCardProps) => {
  const { id } = useParams();

  const { openDialog, closeDialog } = useDialog();
  const { captureAnalytics } = useAnalytics();
  const { palette } = useTheme();

  const [isLocallyExpanded, setIsLocallyExpanded] = useState(false);
  const [customerNoteAnchor, setCustomerNoteAnchor] =
    useState<HTMLElement | null>(null);
  const [internalNoteAnchor, setInternalNoteAnchor] =
    useState<HTMLElement | null>(null);
  const effectiveExpanded = expandedView || isLocallyExpanded;

  const rfqItemId = rest?.id as string | undefined;
  const customerNote = (rest as any)?.customerNote as string | undefined;

  const { mutateAsync: saveCustomerNote } = useSubscribedMutation(
    saveCustomerNoteMutation(),
    [AutoRFQQueryKey.GET_ITEMS]
  );
  const { mutateAsync: deleteCustomerNote } = useSubscribedMutation(
    deleteCustomerNoteMutation(),
    [AutoRFQQueryKey.GET_ITEMS]
  );
  const { mutateAsync: createInternalNote } = useSubscribedMutation(
    createInternalNoteMutation(),
    [AutoRFQQueryKey.GET_INTERNAL_NOTES]
  );
  const { mutateAsync: updateInternalNote } = useSubscribedMutation(
    updateInternalNoteMutation(),
    [AutoRFQQueryKey.GET_INTERNAL_NOTES]
  );
  const { mutateAsync: deleteInternalNote } = useSubscribedMutation(
    deleteInternalNoteMutation(),
    [AutoRFQQueryKey.GET_INTERNAL_NOTES]
  );
  const isNoMatch =
    !matched || rest?.matchOverride === RfqMatchOverride.MarkedAsNonMatched;

  const { competitor, quoteId, updateProcessedProduct } = useRFQStore();
  const { pricing, rfqToQuoteEnabled } = useRfqConfigStore();
  const { configuration } = useCatalogConfig();

  const showStock = configuration?.product?.grid?.stock?.show || false;

  const source = useMemo(
    () => (matched?.stockInfo ? 'catalog' : 'manuall'),
    [matched]
  );
  const isFoundOnlineSelected = useMemo(
    () => foundItem?.name === matched?.name,
    [foundItem?.name, matched?.name]
  );

  const titleReasoning = useMemo(() => {
    if (source === 'catalog') return 'Product was added from catalog.';
    if (isFoundOnlineSelected)
      return 'Product was imported from an online source.';

    return 'Product was added manually.';
  }, [source, isFoundOnlineSelected]);

  const src = useMemo(() => {
    const image =
      matched?.imageUrl ||
      matched?.images?.[0]?.path ||
      '/images/empty/no-product.png';

    if (isFoundOnlineSelected) return '/images/empty/product-found-online.png';

    return image;
  }, [matched, isFoundOnlineSelected]);

  const privateLabelMatched = internalProducts?.filter(
    ({ isPrivateLabel }) => isPrivateLabel
  );

  // Find All privateLabelItems
  const privateLabelItems = useMemo(() => {
    const filteredApItems = privateLabelMatched?.filter(
      pl => pl?.id !== matched?._id && pl?._id !== matched?._id
    );
    const filteredProcessedItems = rest?.matchedInternalProducts?.filter(
      pl => pl?._id !== matched?._id && (pl?.isPrivateLabel || pl?.privateLabel)
    );

    const currItems = privateLabelMatched?.length
      ? filteredApItems
      : filteredProcessedItems;

    return currItems || [];
  }, [privateLabelMatched, rest?.matchedInternalProducts, matched]);

  // Showe Private Label button if there are other options
  const showPrivateLabels = useMemo(() => {
    const totalLabels = privateLabelItems?.length;
    if (!totalLabels) return false;

    return hasPrivateLabels;
  }, [hasPrivateLabels, privateLabelItems]);

  const itemRemovedFromQuotes = useMemo(
    () => quoteId && !rest?.inQuote,
    [quoteId, rest?.inQuote]
  );

  const tirggerPrivateLabels = () => {
    // Capture Analytics
    captureAnalytics(
      {
        action: 'view',
        subAction:
          matched?.isPrivateLabel || matched?.privateLabel
            ? 'Additional Private Labels Found'
            : 'Private Label Found',
        id: matched?.sku || ''
      },
      AnalyticsModules.AutoRFQDetails
    );

    const internalProductsPrivateLabels: any = internalProducts?.filter(
      ({ isPrivateLabel }: { isPrivateLabel?: boolean }) => isPrivateLabel
    );

    openDialog(
      <PrivateLabels
        fetch
        ids={internalProductsPrivateLabels?.map(
          ({ _id }: { _id: string }) => _id
        )}
        competitorName={rest?.productName || rest?.desc}
        competitorSku={rest?.competitorSku}
        products={internalProductsPrivateLabels}
        onClose={() => closeDialog('private-labels')}
        selected={{ ...matched, _id: matched?._id || '' } as Product}
        setSelected={(p: Product) => {
          handleProductChange(p, index - 1, false);
          closeDialog('private-labels');
        }}
      />,
      {
        id: 'private-labels'
      }
    );
  };

  return (
    <Container container alignItems="stretch">
      <Competitor
        manufacturerSku={manufacturerSku}
        ignored={ignored}
        search={search}
        order={order}
        matched={matched}
        expandedView={effectiveExpanded}
        handleManualEntry={handleManualEntry}
        {...rest}
      />

      <Box
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
        <SETVIImage src="/icons/exchange.svg" alt="Exchange" />
      </Box>

      <Grid
        item
        xs
        style={{
          height: '100%'
        }}>
        {isNoMatch && (
          <NoMatch
            handleProductChange={handleProductChange}
            handleManualEntry={handleManualEntry}
            index={index}
            expandedView={effectiveExpanded}
            onExpand={
              !expandedView ? () => setIsLocallyExpanded(true) : undefined
            }
            onCollapse={
              !expandedView && isLocallyExpanded
                ? () => setIsLocallyExpanded(false)
                : undefined
            }
            {...rest}
          />
        )}
        {!isNoMatch &&
          (!effectiveExpanded ? (
            <FlexRow compact>
              <Box
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  gap: 12,
                  alignItems: 'center',
                  width: '100%'
                }}>
                <ImagePreview
                  title={
                    <img
                      src={src}
                      alt="product-img"
                      onError={e => {
                        e.currentTarget.src = '/images/empty/no-product.png';
                      }}
                      style={{
                        width: 180,
                        height: 180,
                        objectFit: 'contain',
                        backgroundColor: palette.common.white,
                        borderRadius: 10,
                        padding: 20
                      }}
                    />
                  }
                  placement="right"
                  arrow>
                  <Box
                    style={{
                      width: 45,
                      height: 45,
                      minWidth: 45,
                      minHeight: 45,
                      maxWidth: 45,
                      maxHeight: 45,
                      borderRadius: 10,
                      border: '1px solid #E0E0E0',
                      overflow: 'hidden',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      backgroundColor: '#fff'
                    }}>
                    <img
                      src={src}
                      alt={matched?.name || 'Product'}
                      onError={e => {
                        e.currentTarget.src = '/images/empty/no-product.png';
                      }}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain'
                      }}
                    />
                  </Box>
                </ImagePreview>

                <Box
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1,
                    gap: 2,
                    minWidth: 0
                  }}>
                  <SHighlightText
                    value={toTitleCase(matched?.name || '')}
                    fontColor="#333D47"
                    searchQuery={search}
                    onClick={() => {
                      if (matched?.external && !matched?.lastModifiedDate)
                        return;
                      if (matched?._id) openProductDetails?.();
                    }}
                    maxLines={1}
                    weight="semibold"
                    style={{
                      cursor:
                        matched?.external && !matched?.lastModifiedDate
                          ? 'inherit'
                          : 'pointer'
                    }}
                  />

                  <Box display="flex" alignItems="center" gridGap={8}>
                    <SHighlightText
                      value={`SKU: ${matched?.sku ? `${matched?.sku}` : '-'}`}
                      searchQuery={search}
                      size="sm"
                      maxLines={1}
                      fontColor="#5D7083"
                    />
                    <Box component="span" style={{ color: '#5D7083' }}>
                      •
                    </Box>
                    <SHighlightText
                      value={`MFG: ${toTitleCase(
                        matched?.vendor?.name || ''
                      )} ${
                        matched?.manufacturerSku
                          ? `(${matched?.manufacturerSku})`
                          : ''
                      }`}
                      searchQuery={search}
                      size="sm"
                      maxLines={1}
                      fontColor="#5D7083"
                    />
                  </Box>
                </Box>

                <Box
                  display="flex"
                  alignItems="center"
                  gridGap={20}
                  style={{ minWidth: 'fit-content' }}>
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="flex-end"
                    gridGap={6}>
                    <Box display="flex" alignItems="center" gridGap={8}>
                      <PricingDisplay
                        cost={matched?.cost}
                        sell={sell}
                        search={search}
                        rfqToQuoteEnabled={rfqToQuoteEnabled}
                        pricingMode={pricing?.mode}
                        showEndDot={
                          !isFoundOnlineSelected && !manualEntry && showStock
                        }
                        defaultMarginEnabled={pricing?.defaultMarginEnabled}
                      />
                      {!isFoundOnlineSelected && !manualEntry && showStock && (
                        <Stock
                          newDesign
                          inventory={matched?.inventory ?? null}
                          isDropship={!!matched?.dropship}
                          isStockProduct={matched?.isStockProduct}
                        />
                      )}
                    </Box>

                    <Box display="flex" alignItems="center" gridGap={8}>
                      <SButton
                        variant="text"
                        size="small"
                        onClick={() => {
                          showMoreProducts(index - 1);

                          captureAnalytics(
                            {
                              action: 'matches',
                              id: matched?._id,
                              total: totalMatched,
                              rfqId: id,
                              ...(!!competitor && {
                                competitorId: competitor?._id || ''
                              })
                            },
                            AnalyticsModules.AutoRFQItem
                          );
                        }}
                        style={{
                          fontSize: '0.875rem',
                          fontWeight: 600,
                          padding: '0 4px',
                          minWidth: 0,
                          minHeight: 0,
                          textTransform: 'none',
                          background: 'transparent'
                        }}>
                        View More Matches
                      </SButton>
                      {matched?.external && !matched?.percentage && (
                        <ExternalChip stockInfo={matched?.stockInfo} />
                      )}
                      {itemRemovedFromQuotes && (
                        <Tooltip title="This Item is removed from Quote" arrow>
                          <RemovedBadge
                            style={{ width: 24, height: 24, minWidth: 24 }}>
                            <SlashedFileIconV2
                              fill="#FA2020"
                              style={{ width: 12, height: 12 }}
                            />
                          </RemovedBadge>
                        </Tooltip>
                      )}
                      {!!matched?.percentage && (
                        <MatchIndicator
                          percentage={matched?.percentage}
                          compact
                        />
                      )}
                      {!!matched?.percentage && (
                        <Tooltip
                          arrow
                          title={matched?.reasoning || ''}
                          disableHoverListener={!matched?.reasoning}>
                          <Box>
                            <AIProductMatchBadge
                              value={matched?.percentage}
                              fontSize={12}
                            />
                          </Box>
                        </Tooltip>
                      )}
                    </Box>
                  </Box>

                  <Box
                    display="flex"
                    alignItems="center"
                    gridGap={18}
                    style={{ alignSelf: 'center' }}>
                    {!!actionButton && actionButton}
                    <IconButton
                      size="small"
                      onClick={() => setIsLocallyExpanded(true)}>
                      <ExpandMoreRoundedIcon
                        htmlColor="#5D7083"
                        style={{ fontSize: 20 }}
                      />
                    </IconButton>
                  </Box>
                </Box>
              </Box>
            </FlexRow>
          ) : (
            <FlexRow>
              <Box
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  gap: 12,
                  width: '100%'
                }}>
                <ImagePreview
                  title={
                    <img
                      src={src}
                      alt="product-img"
                      onError={e => {
                        e.currentTarget.src = '/images/empty/no-product.png';
                      }}
                      style={{
                        width: 180,
                        height: 180,
                        objectFit: 'contain',
                        backgroundColor: palette.common.white,
                        borderRadius: 10,
                        padding: 20
                      }}
                    />
                  }
                  placement="right"
                  arrow>
                  <Box
                    minWidth={110}
                    width={110}
                    minHeight={85}
                    display="flex"
                    alignItems="center"
                    justifyContent="center">
                    <SETVIImage
                      src={src}
                      alt={matched?.name || 'Product'}
                      width={85}
                      height={85}
                      radius={12}
                      fit="contain"
                      style={{
                        maxHeight: 85,
                        maxWidth: 85,
                        border: '1px solid #E0E0E0'
                      }}
                    />
                  </Box>
                </ImagePreview>

                <Box
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 4,
                    flex: 1
                  }}>
                  <Box
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 6
                    }}>
                    <Box
                      style={{
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 6
                      }}>
                      <Box
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6
                        }}>
                        <SHighlightText
                          value={toTitleCase(matched?.name || '')}
                          fontColor="#333D47"
                          searchQuery={search}
                          onClick={() => {
                            if (matched?.external && !matched?.lastModifiedDate)
                              return;
                            if (matched?._id) openProductDetails?.();
                          }}
                          maxLines={1}
                          weight="semibold"
                          style={{
                            cursor:
                              matched?.external && !matched?.lastModifiedDate
                                ? 'inherit'
                                : 'pointer'
                          }}
                        />

                        {(matched?.privateLabel || matched?.isPrivateLabel) && (
                          <ProductLabel />
                        )}

                        {(matched?.vendorHierarchy > 0 ||
                          matched?.vendorRanking > 0) && (
                          <ProductLabel type="strategic-vendor" />
                        )}
                      </Box>
                    </Box>

                    <Box
                      style={{
                        display: 'flex',
                        alignItems: 'flex-end',
                        gap: 12
                      }}>
                      {/* Show not in quotes indicator */}
                      {itemRemovedFromQuotes && (
                        <Tooltip title="This Item is removed from Quote" arrow>
                          <RemovedBadge>
                            <SlashedFileIconV2
                              fill="#FA2020"
                              style={{
                                width: 14,
                                height: 14
                              }}
                            />
                          </RemovedBadge>
                        </Tooltip>
                      )}
                      {!!actionButton && actionButton}
                      {!expandedView && isLocallyExpanded && (
                        <IconButton
                          size="small"
                          onClick={() => setIsLocallyExpanded(false)}>
                          <ExpandMoreRoundedIcon
                            htmlColor="#5D7083"
                            style={{
                              fontSize: 20,
                              transform: 'rotate(180deg)'
                            }}
                          />
                        </IconButton>
                      )}
                    </Box>
                  </Box>

                  <Box
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      gap: 4,
                      alignItems: 'center',
                      color: '#5D7083'
                    }}>
                    <SHighlightText
                      value={`SKU: ${matched?.sku ? `${matched?.sku}` : '-'}`}
                      searchQuery={search}
                      size="sm"
                      maxLines={1}
                      fontColor="#5D7083"
                    />
                    •
                    <SHighlightText
                      value={`MFG: ${toTitleCase(
                        matched?.vendor?.name || ''
                      )} ${
                        matched?.manufacturerSku
                          ? `(${matched?.manufacturerSku})`
                          : ''
                      }`}
                      searchQuery={search}
                      size="sm"
                      maxLines={1}
                      fontColor="#5D7083"
                    />
                    {!!matched?.active && (
                      <ProductStatusBadge
                        style={{
                          minWidth: 'fit-content'
                        }}>
                        <SText title={getProductStatus(matched as Product)} />
                      </ProductStatusBadge>
                    )}
                    {!!matched?.stockInfo && (
                      <ProductStatusBadge>
                        <SText title={getStockInfo(matched?.stockInfo)} />
                      </ProductStatusBadge>
                    )}
                  </Box>

                  <Box
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      gap: 4,
                      alignItems: 'center',
                      color: '#5D7083'
                    }}>
                    <PricingDisplay
                      cost={matched?.cost}
                      sell={sell}
                      search={search}
                      rfqToQuoteEnabled={rfqToQuoteEnabled}
                      pricingMode={pricing?.mode}
                      showEndDot={
                        !isFoundOnlineSelected && !manualEntry && showStock
                      }
                      defaultMarginEnabled={pricing?.defaultMarginEnabled}
                    />

                    <Box style={{ display: 'flex', alignItems: 'center' }}>
                      {!isFoundOnlineSelected && !manualEntry && showStock && (
                        <Stock
                          newDesign
                          inventory={matched?.inventory}
                          isDropship={matched?.dropship}
                          isStockProduct={matched?.isStockProduct}
                        />
                      )}
                    </Box>
                  </Box>
                </Box>
              </Box>
              <Box>
                <AdditionalContent
                  percentage={matched?.percentage}
                  text={
                    !matched?.percentage ? titleReasoning : matched?.reasoning
                  }
                  withBottomBorder
                  prefix={matched?.percentage && 'AI Reasoning:'}
                  customChip={
                    matched?.external &&
                    !matched?.percentage && (
                      <ExternalChip stockInfo={matched?.stockInfo} />
                    )
                  }
                />
              </Box>

              <Box
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}>
                <SButton
                  variant="text"
                  size="small"
                  onClick={() => {
                    showMoreProducts(index - 1);

                    captureAnalytics(
                      {
                        action: 'matches',
                        id: matched?._id,
                        total: totalMatched,
                        rfqId: id,
                        ...(!!competitor && {
                          competitorId: competitor?._id || ''
                        })
                      },
                      AnalyticsModules.AutoRFQItem
                    );
                  }}
                  style={iconStyle}>
                  View More Matches
                </SButton>

                <Box
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12
                  }}>
                  {!!foundOnline && manufacturerSku && rest?.manufacturer && (
                    <SButton
                      variant="text"
                      size="small"
                      disabled={!foundItem}
                      onClick={() => {
                        openDialog(
                          <BaseDialog
                            title="Product Found Online"
                            onClose={closeDialog}
                            elements={[
                              {
                                content: (
                                  <ManualEntry
                                    product={matched as Product}
                                    values={{
                                      url: foundItem?.url,
                                      existinInDb:
                                        foundItem?.isManufacturerExistInDB,
                                      sku: foundItem?.manufacturerSku,
                                      name: foundItem?.name,
                                      price: '',
                                      manufacturer: foundItem?.manufacturer
                                    }}
                                    onClose={closeDialog}
                                    handleSubmit={props => {
                                      handleManualEntry(props, index - 1);
                                    }}
                                  />
                                ),
                                name: 'Manual Entry',
                                key: Tabs.MANUAL
                              }
                            ]}
                          />
                        );

                        captureAnalytics(
                          {
                            action: 'view',
                            subAction: 'Found Online',
                            id: matched?.sku || ''
                          },
                          AnalyticsModules.AutoRFQDetails
                        );
                      }}
                      style={{
                        ...iconStyle
                      }}>
                      Found Online
                    </SButton>
                  )}

                  {showPrivateLabels && (
                    <AnchorBtn variant="text" onClick={tirggerPrivateLabels}>
                      {matched?.isPrivateLabel || matched?.privateLabel
                        ? 'Additional Private Labels Found'
                        : 'Private Label Found'}
                    </AnchorBtn>
                  )}
                </Box>
              </Box>

              <Box
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  gap: 12,
                  width: '100%',
                  marginTop: -4,
                  paddingRight: 0
                }}>
                <SButton
                  variant="text"
                  size="small"
                  startIcon={
                    <Box
                      style={{ position: 'relative', display: 'inline-flex' }}>
                      <CustomerNoteIcon
                        htmlColor="#5D7083"
                        style={{ width: 14, height: 16 }}
                      />
                      {!!customerNote?.trim() && (
                        <Box
                          style={{
                            position: 'absolute',
                            top: -2,
                            right: -3,
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            background: '#FA2020'
                          }}
                        />
                      )}
                    </Box>
                  }
                  onClick={e => setCustomerNoteAnchor(e.currentTarget)}
                  style={{
                    ...iconStyle,
                    fontSize: 12,
                    color: '#5D7083',
                    padding: '0 12px',
                    minWidth: 0
                  }}>
                  Customer Note
                </SButton>

                <SButton
                  variant="text"
                  size="small"
                  startIcon={
                    <Box
                      style={{ position: 'relative', display: 'inline-flex' }}>
                      <InternalNoteIcon
                        htmlColor="#5D7083"
                        style={{ width: 18, height: 18 }}
                      />
                      {(rest?.internalNotesCount || 0) > 0 && (
                        <Box
                          style={{
                            position: 'absolute',
                            top: -2,
                            right: -3,
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            background: '#FA2020'
                          }}
                        />
                      )}
                    </Box>
                  }
                  onClick={e => setInternalNoteAnchor(e.currentTarget)}
                  style={{
                    ...iconStyle,
                    fontSize: 12,
                    color: '#5D7083',
                    padding: '0 12px',
                    minWidth: 0
                  }}>
                  Internal note
                </SButton>
              </Box>
            </FlexRow>
          ))}
      </Grid>

      <Popover
        open={Boolean(customerNoteAnchor)}
        anchorEl={customerNoteAnchor}
        onClose={() => setCustomerNoteAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        PaperProps={{
          style: { borderRadius: 16, marginTop: -8 }
        }}>
        <CustomerNoteDialog
          initialNote={customerNote}
          onClose={() => setCustomerNoteAnchor(null)}
          onSave={async note => {
            if (!rfqItemId) return;
            const response: any = await saveCustomerNote({ rfqItemId, note });
            const nextNote =
              response?.Data?.customerNote ??
              response?.data?.customerNote ??
              response?.customerNote ??
              note;
            updateProcessedProduct(rfqItemId, { customerNote: nextNote });
          }}
          onDelete={async () => {
            if (!rfqItemId) return;
            await deleteCustomerNote({ rfqItemId });
            updateProcessedProduct(rfqItemId, { customerNote: '' });
          }}
        />
      </Popover>

      <Popover
        open={Boolean(internalNoteAnchor)}
        anchorEl={null}
        onClose={() => setInternalNoteAnchor(null)}
        anchorReference="none"
        PaperProps={{
          style: {
            borderRadius: 16,
            position: 'fixed',
            bottom: 24,
            right: 24,
            maxHeight: 'calc(100vh - 48px)',
            display: 'flex',
            flexDirection: 'column'
          }
        }}>
        <InternalNoteDialog
          rfqItemId={rfqItemId}
          onClose={() => setInternalNoteAnchor(null)}
          onSave={async content => {
            if (!rfqItemId) return;
            await createInternalNote({ rfqItemId, content });
            updateProcessedProduct(rfqItemId, {
              internalNotesCount: (rest?.internalNotesCount || 0) + 1
            });
          }}
          onEdit={async (n, content) => {
            if (!rfqItemId) return;
            await updateInternalNote({
              rfqItemId,
              noteId: n._id,
              content
            });
          }}
          onDelete={async n => {
            if (!rfqItemId) return;
            await deleteInternalNote({ rfqItemId, noteId: n._id });
            updateProcessedProduct(rfqItemId, {
              internalNotesCount: Math.max(
                (rest?.internalNotesCount || 0) - 1,
                0
              )
            });
          }}
        />
      </Popover>
    </Container>
  );
};

export default memo(ProductCard);
